import React from 'react';
import PropTypes from 'prop-types';
import { FaFolder, FaEllipsisV, FaEdit, FaTrash } from 'react-icons/fa';

const CLASS_OPTIONS = [
  { value: 'class6', display: 'Class 6th' },
  { value: 'class9', display: 'Class 9th' }
];

const FolderGrid = ({ 
  folders, 
  showActions, 
  activeActionMenu, 
  onFolderClick, 
  onToggleActionMenu, 
  onEditFolder, 
  onDeleteFolder 
}) => {
  const styles = {
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '1rem',
      marginBottom: '2rem'
    },
    folderCard: {
      backgroundColor: 'white',
      borderRadius: '0.5rem',
      border: '1px solid #E5E7EB',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s',
      position: 'relative'
    },
    folderCardContent: {
      padding: '1rem'
    },
    folderHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '0.5rem'
    },
    folderNameArea: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      flex: '1 1 auto',
      minWidth: 0,
      overflow: 'hidden'
    },
    folderIcon: {
      color: '#F59E0B',
      marginRight: '0.75rem',
      fontSize: '1.25rem',
      flexShrink: 0
    },
    folderName: {
      fontWeight: '500',
      color: '#1F2937',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    actionButtonContainer: {
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
      position: 'relative'
    },
    actionButton: {
      color: '#6B7280',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '0.5rem',
      borderRadius: '0.375rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s'
    },
    actionMenu: {
      position: 'absolute',
      top: '100%',
      right: 0,
      backgroundColor: 'white',
      border: '1px solid #E5E7EB',
      borderRadius: '0.5rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      zIndex: 10,
      minWidth: '120px',
      overflow: 'hidden'
    },
    actionMenuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      color: '#374151',
      fontSize: '0.875rem',
      width: '100%',
      textAlign: 'left',
      border: 'none',
      background: 'none'
    },
    deleteMenuItem: {
      color: '#EF4444'
    },
    folderDate: {
      fontSize: '0.75rem',
      color: '#6B7280',
      marginTop: '0.5rem'
    },
    folderMeta: {
      display: 'flex',
      gap: '0.5rem',
      marginTop: '0.5rem',
      flexWrap: 'wrap'
    },
    metaBadge: {
      padding: '0.25rem 0.5rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      backgroundColor: '#F3F4F6',
      color: '#4B5563'
    }
  };

  return (
    <div style={styles.gridContainer}>
      {folders.map(folder => (
        <div 
          key={folder.id}
          style={styles.folderCard}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
          }}
        >
          <div style={styles.folderCardContent}>
            <div style={styles.folderHeader}>
              <div 
                style={styles.folderNameArea}
                onClick={() => onFolderClick(folder)}
              >
                <FaFolder style={styles.folderIcon} />
                <span style={styles.folderName}>{folder.name}</span>
              </div>
              
              {showActions && (
                <div className="action-menu-container" style={styles.actionButtonContainer}>
                  <button 
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleActionMenu(folder.id);
                    }}
                    style={styles.actionButton}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <FaEllipsisV />
                  </button>
                  
                  {activeActionMenu === folder.id && (
                    <div style={styles.actionMenu}>
                      <button 
                        type="button"
                        style={styles.actionMenuItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditFolder(folder);
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <FaEdit /> Edit
                      </button>
                      <button 
                        type="button"
                        style={{...styles.actionMenuItem, ...styles.deleteMenuItem}}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteFolder(folder);
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={styles.folderDate}>
              Updated: {new Date(folder.updated_at).toLocaleDateString()}
            </div>
            
            <div style={styles.folderMeta}>
              {folder.class && (
                <span style={styles.metaBadge}>
                  Class: {CLASS_OPTIONS.find(option => option.value === folder.class)?.display || folder.class}
                </span>
              )}
              {folder.subject && (
                <span style={styles.metaBadge}>
                  Subject: {folder.subject}
                </span>
              )}
              {folder.course_target && (
                <span style={styles.metaBadge}>
                  Target: {folder.course_target}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

FolderGrid.propTypes = {
  folders: PropTypes.arrayOf(PropTypes.object).isRequired,
  showActions: PropTypes.bool.isRequired,
  activeActionMenu: PropTypes.number,
  onFolderClick: PropTypes.func.isRequired,
  onToggleActionMenu: PropTypes.func,
  onEditFolder: PropTypes.func,
  onDeleteFolder: PropTypes.func
};

export default FolderGrid;
